import { ViewOptionSwitch } from "@/components/ui/view-options/view-options-switch";
import { changeMode } from "@/store/mode-slice";
import { selectModeName } from "@/store/mode-selectors";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectObjectVisibility } from "@/store/view-options/view-options-selectors";
import {
  setObjectVisibility,
  ViewObjectTypes,
} from "@/store/view-options/view-options-slice";
import {
  cyan,
  Dropdown,
  FaroButton,
  FaroText,
  grey,
  Option,
} from "@faro-lotv/flat-ui";
import { validateEnumValue } from "@faro-lotv/foundation";
import { selectDashboardUrl, selectProjectId } from "@faro-lotv/project-source";
import { Divider, Link, Stack } from "@mui/material";
import type { JSX } from "react";
import { useProjectGeoPosition } from "./use-project-geo-position";
import { WorldMapPrototypeNote } from "./world-map-alignment-panel";
import { useCurrentProjectWorldMap } from "./world-map-renderer";
import { useWorldMapStore } from "./world-map-store";
import { WorldMapStyle } from "./world-map-tiles";

const STYLE_OPTIONS: Option[] = Object.values(WorldMapStyle).map((style) => ({
  key: style,
  value: style,
  label: style,
  isDisabled: false,
}));

/** @returns The Sheets menu entry to show the world map as an additional layer in the 2D and 3D views */
export function WorldMapLayerOption({
  onAlignmentStarted,
}: {
  /** Called when the user starts aligning the map, e.g. to close the menu */
  onAlignmentStarted?(): void;
}): JSX.Element {
  const dispatch = useAppDispatch();
  const isVisible = useAppSelector(
    selectObjectVisibility(ViewObjectTypes.worldMap),
  );
  const position = useProjectGeoPosition();
  const { pose } = useCurrentProjectWorldMap();
  const { style, setStyle, setPose, startAlignment } = useWorldMapStore();
  const dashboardUrl = useAppSelector(selectDashboardUrl);
  const projectId = useAppSelector(selectProjectId);
  const modeName = useAppSelector(selectModeName);

  // Points are picked top-down, so the alignment always runs in the 2D view
  function startAligning(): void {
    if (modeName !== "sheet") dispatch(changeMode("sheet"));
    startAlignment();
    onAlignmentStarted?.();
  }

  return (
    <>
      <Divider sx={{ backgroundColor: grey[800], my: "0 !important" }} />
      <Stack
        sx={{ gap: 1, px: 1.5, py: 1 }}
        aria-label="World map visibility toggle"
      >
        <ViewOptionSwitch
          label="World map"
          variant="heading14"
          isChecked={isVisible && !!position}
          onToggled={(visibility) => {
            if (!position) return;
            dispatch(
              setObjectVisibility({
                type: ViewObjectTypes.worldMap,
                visibility,
              }),
            );
          }}
        />
        {position === null && (
          <FaroText variant="bodyS" dark>
            No coordinates set for this project.{" "}
            {dashboardUrl && projectId && (
              <Link
                href={`${dashboardUrl}/projects/${projectId}/details`}
                target="_blank"
                rel="noreferrer"
                color={cyan[400]}
              >
                Add coordinates in the Dashboard
              </Link>
            )}
          </FaroText>
        )}
        {isVisible && position && (
          <>
            <Stack
              direction="row"
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <FaroText variant="bodyM" dark>
                Map style
              </FaroText>
              <Dropdown
                dark
                variant="underlined"
                fullWidth={false}
                shouldCapitalize={false}
                options={STYLE_OPTIONS}
                value={style}
                onChange={(e) => {
                  if (validateEnumValue(e.target.value, WorldMapStyle)) {
                    setStyle(e.target.value);
                  }
                }}
              />
            </Stack>
            <Stack
              direction="row"
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <FaroText variant="bodyM" dark>
                {pose ? "Aligned by you" : "Not aligned yet"}
              </FaroText>
              <Stack direction="row" sx={{ gap: 1 }}>
                {pose && (
                  <FaroButton
                    variant="ghost"
                    size="s"
                    dark
                    onClick={() => setPose(undefined)}
                  >
                    Reset
                  </FaroButton>
                )}
                <FaroButton size="s" dark onClick={startAligning}>
                  Align map
                </FaroButton>
              </Stack>
            </Stack>
            <FaroText variant="bodyXS" dark>
              Map data © Esri, OpenStreetMap contributors and their data
              providers
            </FaroText>
          </>
        )}
        {position && <WorldMapPrototypeNote />}
      </Stack>
    </>
  );
}
